import React, { memo, useEffect, useMemo, useState } from 'react';
import { Keyboard, Platform, StyleSheet } from 'react-native';

import { BottomTabBarProps, createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { EventArg } from '@react-navigation/native';

import { SocialFeedIcon } from '@/assets/icons/socialFeed';
import { ArcadeTabIcon } from '@/assets/icons/tab/ArcadeTabIcon';
import { CheckmarkOutlinedIcon } from '@/assets/icons/tab/checkmark-outlined';
import { LobbyIcon } from '@/assets/icons/tab/home';
import { SearchTabIcon } from '@/assets/icons/tab/search';
import { Box } from '@/components/lib/components/Box';
import { EntriesTopTabBarScreen } from '@/feature/entries-pickem/screens/EntriesTopTabBarScreen';
import { useFantasyEntryImport } from '@/feature/entry-share/hooks/use-fantasy-entry-share';
import { LobbyPickbar } from '@/feature/lobby/components/LobbyPickbar';
import { PickTypeBadgesEducationProvider } from '@/feature/lobby/components/PickTypeBadgesEducationProvider';
import { RaceToPlaceSheetProvider } from '@/feature/lobby/components/race-to-place/RaceToPlaceProvider';
import { usePartnerLinkImport } from '@/feature/partner-deeplink/hooks/use-partner-link-import';
import { useProductTransitionStore } from '@/feature/product-transition/use-product-transition-store';
import { SearchTabTooltip } from '@/feature/search/components/SearchTabTooltip';
import { PicksSearchScreen } from '@/feature/search/screens/PicksSearch';
import { FeedTabTooltip } from '@/feature/social-feed/FeedTabTooltip';
import { SocialFeedScreen } from '@/feature/social-feed/SocialFeedScreen';
import { useAppInit } from '@/hooks/use-app-init';
import { useGlobalState } from '@/hooks/use-global-state';
import { Product, useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { useOneLinkNavigation } from '@/hooks/use-one-link-navigation';
import { ComposeProviders } from '@/providers/ComposeProviders';

import { AccountTabConfig, TabBar, TabOptions, mapTabsFromTabSettings } from '../tabs';
import { LobbyStack } from './lobbyStack';
import { PickemTabNavigatorParamList } from './types';

// Add here Picks specific providers
const PicksProviders = [RaceToPlaceSheetProvider, PickTypeBadgesEducationProvider];

const TabPickem = createBottomTabNavigator<PickemTabNavigatorParamList>();

const PickemTabsConfig = [
    { name: 'Lobby', component: LobbyStack, icon: LobbyIcon, title: 'Home' },
    { name: 'Feed', component: SocialFeedScreen, icon: SocialFeedIcon, title: 'Feed' },
    { name: 'FantasySearch', component: PicksSearchScreen, icon: SearchTabIcon, title: 'Search' },
    { name: 'Entries', component: EntriesTopTabBarScreen, icon: CheckmarkOutlinedIcon, title: 'Track' },
] as const;

const PicksTabBar = (props: BottomTabBarProps): React.JSX.Element => <PickemTabBar {...props} />;

export const PickemTabsNavigator = () => {
    useFantasyEntryImport();

    const tabSettings = useJurisdictionStore(state => state.jurisdictionSettings?.productConfig?.tabSettings);
    const socialFeedPrismicFlag = useJurisdictionStore(
        store => store.jurisdictionSettings?.productConfig?.settings?.social_feed_enabled
    );
    const socialFeedAPIEnabled = useGlobalState(state => state.entryFeedConfig?.socialFeedEnabled);
    const socialFeedEnabled = socialFeedPrismicFlag && socialFeedAPIEnabled;

    const tabsConfig = useMemo(() => {
        const baseTabs = socialFeedEnabled
            ? PickemTabsConfig
            : [...PickemTabsConfig.filter(tab => tab.name !== 'Feed'), ArcadeSwitchTab];

        return mapTabsFromTabSettings([...baseTabs, AccountTabConfig].filter(Boolean), tabSettings);
    }, [tabSettings, socialFeedEnabled]);

    usePartnerLinkImport();
    useOneLinkNavigation();

    useAppInit();

    return (
        <ComposeProviders providers={PicksProviders}>
            <TabPickem.Navigator tabBar={PicksTabBar} screenOptions={TabOptions}>
                {tabsConfig.map(it => (
                    <TabPickem.Screen key={it.name} {...it} options={{ tabBarIcon: it.icon, title: it.title }} />
                ))}
            </TabPickem.Navigator>
        </ComposeProviders>
    );
};

/**
 * Hook used to hide the tab bar when the keyboard is open on Android
 */
const useTabBarVisibility = () => {
    const [showTabBar, setShowTabBar] = useState(true);

    useEffect(() => {
        const showSubscription = Keyboard.addListener('keyboardDidShow', () => {
            setShowTabBar(false);
        });
        const hideSubscription = Keyboard.addListener('keyboardDidHide', () => {
            setShowTabBar(true);
        });

        return () => {
            showSubscription.remove();
            hideSubscription.remove();
        };
    }, []);

    return Platform.OS === 'android' ? showTabBar : true;
};

// ideally we should not hide the tab bar manually.
// there's a prop for that called tabBarHideOnKeyboard
// but it doesn't work if we render a custom tab bar.
const PickemTabBar = memo((props: BottomTabBarProps) => {
    const showTabBar = useTabBarVisibility();

    if (!showTabBar) {
        return null;
    }

    return (
        <>
            {/* collapsable={false} is needed because RN collapses views that have no visual content,
               which breaks z-index stacking on android and causes LobbyPickbar (which has zIndex: 100) to render above the tab bar. */}
            <Box collapsable={false} style={StyleSheet.absoluteFillObject} pointerEvents="box-none">
                <LobbyPickbar />
            </Box>
            <FeedTabTooltip state={props.state} navigation={props.navigation} />
            <SearchTabTooltip state={props.state} navigation={props.navigation} />
            <TabBar {...props} />
        </>
    );
});

PickemTabBar.displayName = 'PickemTabBar';

const EmptyScreen = () => null;

const ArcadeSwitchTab = {
    name: 'SwitchToArcade',
    component: EmptyScreen,
    icon: () => <ArcadeTabIcon />,
    title: 'Arcade',
    listeners: () => ({
        tabPress: (e: EventArg<'tabPress', true>) => {
            // Prevent default navigation to the tab's screen
            e.preventDefault();
            useProductTransitionStore.getState().actions.triggerTransition(Product.Arcade);
        },
    }),
} as const;
